import { useCallback, useEffect, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { Link } from 'react-router-dom';
import { Alert, Button, ButtonGroup, Col, Form, Modal, ProgressBar, Row, Table } from 'react-bootstrap';
import type {
  FulfillmentAdminClient,
  MarketingCarrierBooking,
  MarketingCarrierBookingResult,
  MarketingImportBatch,
  MarketingImportBookingMode,
  MarketingImportBookingOptions,
  MarketingImportColumn,
  MarketingImportColumnNeed,
  MarketingImportDuplicate,
  MarketingImportOrderResult,
  MarketingImportPreview,
  MarketingImportSheetType,
  MarketingStockSource,
  MarketingWaybillCarrier,
} from '@/infrastructure/FulfillmentAdminClient';
import { OperatorIdentity } from '@/infrastructure/OperatorIdentity';
import { duplicateFrom } from '@/marketing/lib/errorMessage';
import {
  addressWarningText,
  mergeNoticeText,
  STOCK_CHECK_UNAVAILABLE_TEXT,
  stockWarningText,
} from '@/utils/fulfillmentMarketing';
import {
  MARKETING_IMPORT_ACCEPT,
  alignToTemplate,
  downloadMarketingImportTemplate,
  isYunExpressBulkSheet,
  readAsCsv,
} from '@/utils/marketingImportFile';
import { EMPTY_CELL } from '@/utils/fulfillmentOrderColumns';
import { kstHourMinute, kstTimestamp } from '@/utils/fulfillmentTime';
import { describeFailure } from '@/utils/fulfillmentErrors';
import { EXTERNAL_WAYBILL_RECORDED } from '@/utils/fulfillmentCarrierVoid';
import { currentOutboundShipmentOf, DELEONEX_NEEDS_ATTENTION, isPendingParcel } from '@/utils/fulfillmentDeleoLabel';
import { useAuth } from '@/providers/AuthProvider';
import { ROUTES } from '@/routes/routes';
import { CampaignGoodsArrivalDialog } from '@/pages/fulfillment/components/CampaignGoodsArrivalDialog';
import { FulfillmentToastHost } from '@/pages/fulfillment/components/FulfillmentToast';
import { useFulfillmentToast } from '@/pages/fulfillment/components/useFulfillmentToast';
import {
  CheckIcon,
  DownloadIcon,
  PencilIcon,
  UploadIcon,
  WarningIcon,
} from '@/pages/fulfillment/components/UltimateIcons';
import { ULTIMATE_MODAL } from '@/pages/fulfillment/components/ultimateModal';
import { CarrierErrorText } from '@/pages/fulfillment/components/CarrierErrorText';
import '../../ultimateDesign.css';
import { BOOKING_LANES, looksRateLimited, runBookingLanes } from './bookingLanes';
import './addOrdersModal.css';

const GENERIC_ERROR = 'The marketing operations request failed.';
/** A file can carry hundreds of rows; the desk scans the head of it and reads the rest as reasons below. */
const PREVIEW_ROWS = 50;
/** The preset the desk reaches for almost every time; anything else is typed in [wireframe M1]. */
const SEEDING_ORDER_TYPE = 'Influencer Seeding';
/** The server refuses a longer order type or PIC, so the box stops there instead of the confirm. */
const BATCH_FIELD_MAX_LENGTH = 128;
/** Enough of the import record to find the batch just confirmed and the ones around it. */
const RECENT_IMPORTS = 10;

/** The three answers to "do I have to fill this in", in the desk's words; the note says when "sometimes" is. */
const NEED_LABEL: Record<MarketingImportColumnNeed, string> = {
  required: 'Required',
  conditional: 'In some cases',
  optional: 'Optional',
};

/**
 * Who books the parcels is read off the file (SSD-7596): waybills on every row mean they were booked outside, none
 * means OMS books them. Before a preview, and for a file that mixes the two (refused row by row), the rule is the line.
 */
const BOOKING_LINE: Record<MarketingImportBookingMode, string> = {
  FF2: 'OMS books the carrier for these orders.',
  EXTERNAL: 'The waybills are in the file, so OMS records them and books nothing.',
  AUTO: 'A file with waybills is recorded as booked outside; a file without them is booked by OMS.',
};

/** The same answer beside Confirm, in a few words. */
const BOOKING_SUMMARY: Record<MarketingImportBookingMode, string> = {
  FF2: 'OMS books the carrier',
  EXTERNAL: 'Already booked outside',
  AUTO: 'Waybills on some rows only',
};

/**
 * Where the batch's goods come from (SSD-7458), in the desk's words, with what each choice does. FF2 stock is what every
 * import did before the choice existed.
 */
const STOCK_SOURCE_CHOICES: ReadonlyArray<{ source: MarketingStockSource; label: string; help: string }> = [
  {
    source: 'FF2_STOCK',
    label: 'FF2 stock',
    help: 'Lines take FF2 stock; what stock cannot cover in full is bought.',
  },
  {
    source: 'CAMPAIGN_SUPPLIED',
    label: 'Campaign supplied',
    help: 'The brand sends the goods. Lines wait for them without taking FF2 stock and nothing is bought; press Goods arrived once they are on a shelf.',
  },
];

const STOCK_SOURCE_LABEL: Record<MarketingStockSource, string> = {
  FF2_STOCK: 'FF2 stock',
  CAMPAIGN_SUPPLIED: 'Campaign supplied',
};

/** The carriers a waybill booked outside can belong to; YunExpress first, since the bulk sheet is theirs. */
const WAYBILL_CARRIERS: ReadonlyArray<{ value: MarketingWaybillCarrier; label: string }> = [
  { value: 'YUN_EXPRESS', label: 'YunExpress' },
  { value: 'DELEO', label: 'Deleo' },
  { value: 'FEDEX', label: 'FedEx' },
  { value: 'PARXL', label: 'PARXL' },
];

/** The sheet the server read the file as. */
const SHEET_LABEL: Record<MarketingImportSheetType, string> = {
  FF2_TEMPLATE: 'FF2 template',
  YUNEXPRESS_BULK: 'YunExpress bulk sheet',
};

/** How each order's booking reads in the result table, and the colour that tells the desk whether to act. */
const BOOKING_STATUS: Record<MarketingCarrierBookingResult, { label: string; tone: string }> = {
  BOOKED: { label: 'Booked', tone: 'ao-status-done' },
  EXTERNAL: { label: 'Booked outside', tone: 'ao-status-done' },
  FAILED: { label: 'Failed', tone: 'ao-status-act' },
  UNKNOWN: { label: 'Check carrier', tone: 'ao-status-check' },
  SKIPPED: { label: 'Not booked', tone: 'text-muted' },
};

/**
 * The batch's choices as the server takes them: the file decides who books (AUTO, SSD-7596), and a carrier for the
 * waybills travels only once the desk named one, never by default: a Deleo file without a carrier column recorded as
 * YunExpress would be traced at the wrong carrier, so the server refuses those rows until it is named. FF2 carries no
 * carrier. The stock source travels only when it is not FF2 stock, the server's default (a server from before SSD-7458
 * refuses the field).
 */
const bookingOf = (
  carrier: MarketingWaybillCarrier | '',
  stockSource: MarketingStockSource = 'FF2_STOCK',
  mode: MarketingImportBookingMode = 'AUTO',
): MarketingImportBookingOptions => ({
  bookingMode: mode,
  ...(mode !== 'FF2' && carrier ? { externalCarrier: carrier } : {}),
  ...(stockSource === 'CAMPAIGN_SUPPLIED' ? { stockSource } : {}),
});

/** The same choices under another booking mode. */
const withMode = (booking: MarketingImportBookingOptions, mode: MarketingImportBookingMode) =>
  bookingOf(booking.externalCarrier ?? '', booking.stockSource, mode);

/**
 * A server from before SSD-7596 refuses AUTO outright with class-validator's enum message, before the handler runs, so
 * nothing was read or created; the screen then asks again with FF2 and carries on as every import did before. Any other
 * 400 (a mixed file's "Row N: …" refusal among them) is the server's answer and is shown as it is.
 */
const autoRefused = (booking: MarketingImportBookingOptions, error: unknown): boolean =>
  booking.bookingMode === 'AUTO' &&
  isAxiosError(error) &&
  error.response?.status === 400 &&
  /bookingMode must be one of/.test(JSON.stringify(error.response.data ?? ''));

/**
 * Orders sent per "Book all not attempted" request. The server books four at a time inside its own 240-second budget,
 * so eight come back in about twenty seconds on the carrier sandbox, the table fills in as each answer lands, and no
 * request comes near the load balancer's 300-second timeout however long the file (SSD-7512).
 */
const REBOOK_CHUNK = 8;

/**
 * How long the box waits after the last keystroke before it previews what was typed or pasted: long enough not to
 * preview a half-typed line, short enough that the desk never has to press anything before Confirm (SSD-7512).
 */
const PREVIEW_DELAY_MS = 400;

/**
 * An order the confirm left for this screen to book (SSD-7512): the server answers SKIPPED with no auto-booker state
 * when it was asked to defer, so a long file comes back in seconds and its bookings run here, eight at a time, with
 * the progress on screen instead of one request that holds the dialog for minutes.
 */
const deferredByServer = (booking: MarketingCarrierBooking | undefined): boolean =>
  booking?.result === 'SKIPPED' && booking.state === null;

/**
 * A server from before SSD-7512 refuses the deferBooking field outright (unknown properties are rejected before the
 * handler runs, so nothing was created); the screen then confirms again without it and the server books as it did.
 */
const deferralRefused = (error: unknown): boolean =>
  isAxiosError(error) &&
  error.response?.status === 400 &&
  JSON.stringify(error.response.data ?? '').includes('deferBooking');

/** How far a booking run has got, for the progress bar. */
interface BookingProgress {
  done: number;
  total: number;
  /** When the run began, for the time it has left. */
  startedAt: number;
}

/**
 * An order no carrier was asked about: the confirm's time budget ran out before its turn, or it failed before the
 * carrier call. The auto-booker then gives no state, so no parcel can exist and booking it again in bulk is safe. A
 * carrier refusal or timeout carries the auto-booker's state and keeps its own Book again: a YunExpress timeout may
 * have made a parcel, so the desk looks it up first.
 */
const notAttempted = (booking: MarketingCarrierBooking | undefined): boolean =>
  booking?.result === 'FAILED' && booking.state === null;

/** A connection that never opened: the request cannot have reached the booking. */
const NEVER_SENT = ['ECONNREFUSED', 'ENOTFOUND', 'EAI_AGAIN'];

/**
 * Whether a booking request that failed may still have left a parcel at the carrier, as the server sorts its own
 * answers: 409 means the order already holds a carrier order or another booking is running, a 502 with outcomeUnknown
 * or any other 5xx may have made one, and a request with no answer may have been served. A 4xx refusal, or a connection
 * that never opened, means the booking never ran (SSD-7512).
 */
const mayHaveBooked = (error: unknown): boolean => {
  if (!isAxiosError<{ outcomeUnknown?: boolean; code?: string }>(error)) {
    return true;
  }
  const status = error.response?.status;
  if (status === undefined) {
    return !NEVER_SENT.includes(error.code ?? '');
  }
  if (status === 502) {
    return error.response?.data?.outcomeUnknown === true;
  }
  // A waybill booked outside OMS stops the booking before any carrier is called (SSD-7586).
  if (status === 409 && error.response?.data?.code === EXTERNAL_WAYBILL_RECORDED) {
    return false;
  }
  return status === 409 || status >= 500;
};

/** Counts sit in running text here, and "1 errors" reads as a bug in the screen rather than in the file. */
const plural = (count: number, word: string): string => `${count} ${word}${count === 1 ? '' : 's'}`;

/** "150 orders: 144 Booked · 6 Booking": how the import's orders stand, in the words of the result table. */
const countLine = (orders: MarketingImportOrderResult[], inFlight: ReadonlySet<number>): string => {
  const counts = new Map<string, number>();
  for (const order of orders) {
    const booking = order.carrierBooking;
    const label = inFlight.has(order.orderId)
      ? 'Booking'
      : !booking
        ? 'No answer'
        : notAttempted(booking)
          ? 'Not attempted'
          : BOOKING_STATUS[booking.result].label;
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  return `${plural(orders.length, 'order')}: ${[...counts].map(([label, count]) => `${count} ${label}`).join(' · ')}`;
};

/** Where an order of the import stands for the desk: done, still with the screen or the carrier, or waiting for a person. */
type RunState = 'booked' | 'working' | 'attention';

/** The four tiles above the result double as its filter; "attention" is everything a person has to look at. */
type RunFilter = 'all' | RunState;

/**
 * An order is "working" while its booking is with the server: a booking run marks every order it will reach when it
 * starts, so one whose request was refused at the door shows its reason at once, not after the rest of the run.
 */
const runStateOf = (order: MarketingImportOrderResult, inFlight: ReadonlySet<number>): RunState => {
  const booking = order.carrierBooking;
  if (inFlight.has(order.orderId)) {
    return 'working';
  }
  if (booking?.result === 'BOOKED' || booking?.result === 'EXTERNAL') {
    return 'booked';
  }
  return 'attention';
};

/** What the confirm is turning into orders right now: the rows of the file and the orders they fold into. */
interface CreatingRun {
  rows: number;
  orders: number;
  /** "Import anyway" on a file already imported: orders already in OMS are matched, so nothing may be created. */
  again: boolean;
}

/**
 * "about 3 min left", from how long the run took per order so far. Nothing until the first answer lands: one request
 * of eight carries the carrier's own warm-up, so an estimate from nothing would be a guess dressed as a number.
 */
const timeLeft = (progress: BookingProgress, now: number): string | null => {
  if (progress.done === 0 || progress.done >= progress.total) {
    return null;
  }
  const seconds = (((now - progress.startedAt) / progress.done) * (progress.total - progress.done)) / 1000;
  return seconds < 60 ? 'under a minute left' : `about ${Math.ceil(seconds / 60)} min left`;
};

/** The import's waybills as CSV text: what Download result saves. */
export const resultCsv = (orders: MarketingImportOrderResult[]): string => {
  const cell = (value: string | number | null | undefined) => {
    const text = String(value ?? '');
    // A cell that starts like a formula is a formula to Excel; the leading quote keeps it text.
    const safe = /^[=+\-@\t\r\n]/.test(text) ? `'${text}` : text;
    return `"${safe.replace(/"/g, '""')}"`;
  };
  const lines = [
    ['Order', 'External No.', 'Carrier', 'Waybill', 'Status', 'Reason'].map(cell).join(','),
    ...orders.map((order) => {
      const booking = order.carrierBooking;
      return [
        order.orderId,
        order.externalOrderNumber,
        booking?.carrier,
        booking?.trackingNumber,
        booking ? BOOKING_STATUS[booking.result].label : '',
        booking?.error,
      ]
        .map(cell)
        .join(',');
    }),
  ];
  // The byte-order mark makes Excel read the file as UTF-8 rather than guess.
  return `\uFEFF${lines.join('\n')}`;
};

/** The import's waybills as a sheet, for the desk that hands them on to the influencer team. */
const downloadResult = (orders: MarketingImportOrderResult[], fileName: string) => {
  const blob = new Blob([resultCsv(orders)], { type: 'text/csv;charset=utf-8' });
  if (typeof URL.createObjectURL !== 'function') {
    return;
  }
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${fileName.replace(/\.[^.]+$/, '') || 'marketing-import'} result.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

interface ImportRunProps {
  readonly orders: MarketingImportOrderResult[];
  /** Set while the confirm is with the server. */
  readonly creating: CreatingRun | null;
  /** Set while a booking run is under way. */
  readonly progress: BookingProgress | null;
  /** Orders whose booking is with the server right now; they count as Booking, not as what they were. */
  readonly inFlight: ReadonlySet<number>;
  readonly bulkBooking: boolean;
  readonly checkingCarrier: boolean;
  /** The parcels were booked outside: nothing is booked here, the waybills are only recorded. */
  readonly external: boolean;
  /** The brand sends the goods: the orders wait for them rather than taking FF2 stock (SSD-7458). */
  readonly campaignSupplied: boolean;
  readonly fileName: string;
  readonly onBookAll: () => void;
  readonly onCheckAgain: () => void;
  readonly onBookAgain: (orderId: number) => void;
  readonly onImportAnother: () => void;
}

/**
 * The import as it runs and as it ends, right under Confirm where the desk's eyes are (10/20 dev test: the result used
 * to land below a 450-row preview, so a finished run looked frozen and a booked one looked like it had not started).
 * Three steps, one bar, four counts that filter the table, and the waybills in a table that scrolls inside itself.
 */
function ImportRun({
  orders,
  creating,
  progress,
  inFlight,
  bulkBooking,
  checkingCarrier,
  external,
  campaignSupplied,
  fileName,
  onBookAll,
  onCheckAgain,
  onBookAgain,
  onImportAnother,
}: ImportRunProps) {
  const [filter, setFilter] = useState<RunFilter>('all');
  const running = progress !== null;
  // A single Book again or a Check carrier again is work under way too: the run is not done while any of it is.
  const active = running || inFlight.size > 0 || checkingCarrier;
  const states = new Map(orders.map((order) => [order.orderId, runStateOf(order, inFlight)]));
  const count = (state: RunState) => orders.filter((order) => states.get(order.orderId) === state).length;
  const booked = count('booked');
  const working = count('working');
  const attention = count('attention');
  const created = orders.filter((order) => order.created).length;
  const waiting = orders.filter((order) => notAttempted(order.carrierBooking) && !inFlight.has(order.orderId)).length;
  const checking = orders.filter((order) => order.carrierBooking?.result === 'UNKNOWN').length;
  const phase = creating ? 'create' : active ? 'book' : 'done';
  const shown = filter === 'all' ? orders : orders.filter((order) => states.get(order.orderId) === filter);
  const steps: ReadonlyArray<{ key: 'create' | 'book' | 'done'; label: string; detail: string }> = [
    {
      key: 'create',
      label: creating?.again ? 'Import again' : 'Create orders',
      detail: `${
        creating
          ? `${plural(creating.rows, 'row')} → ${plural(creating.orders, 'order')}`
          : created === orders.length
            ? `${plural(orders.length, 'order')} created`
            : `${created} new · ${orders.length - created} already in OMS`
      }${campaignSupplied ? ' · campaign supplied' : ''}`,
    },
    {
      key: 'book',
      label: external ? 'Record waybills' : 'Book carriers',
      detail: progress ? `${progress.done} / ${progress.total}` : creating ? 'Next' : `${booked} / ${orders.length}`,
    },
    {
      key: 'done',
      label: 'Done',
      detail: phase === 'done' ? (attention > 0 ? `${attention} need you` : 'All set') : '',
    },
  ];
  const at: Record<typeof phase, number> = { create: 0, book: 1, done: 2 };
  const tiles: ReadonlyArray<{ key: RunFilter; label: string; value: number; tone: string }> = [
    { key: 'all', label: 'Orders', value: orders.length, tone: 'ao-tile-all' },
    { key: 'booked', label: external ? 'Recorded' : 'Booked', value: booked, tone: 'ao-tile-done' },
    { key: 'working', label: 'Booking', value: working, tone: 'ao-tile-working' },
    { key: 'attention', label: 'Needs you', value: attention, tone: 'ao-tile-act' },
  ];
  const left = progress ? timeLeft(progress, Date.now()) : null;

  return (
    <section className="ao-run" aria-label="This import" data-testid="marketing-confirm-progress">
      <ol className="ao-steps">
        {steps.map((step, index) => (
          <li
            key={step.key}
            className={
              phase === 'done' && index === 2 && attention > 0
                ? 'is-act'
                : index < at[phase] || (phase === 'done' && index === 2)
                  ? 'is-done'
                  : index === at[phase]
                    ? 'is-now'
                    : ''
            }
            aria-current={index === at[phase] ? 'step' : undefined}
          >
            <span className="ao-step-dot">
              {phase === 'done' && index === 2 && attention > 0 ? (
                <WarningIcon />
              ) : index < at[phase] || phase === 'done' ? (
                <CheckIcon />
              ) : (
                index + 1
              )}
            </span>
            <span className="ao-step-text">
              <span className="ao-step-label">{step.label}</span>
              <span className="ao-step-detail">{step.detail}</span>
            </span>
          </li>
        ))}
      </ol>
      <div className="ao-run-status" role="status">
        {creating ? (
          <>
            <span className="ao-run-line">
              {creating.again
                ? `Importing ${plural(creating.rows, 'row')} again: orders already in OMS are matched, not made twice…`
                : `Creating ${plural(creating.orders, 'order')} from ${plural(creating.rows, 'row')}…`}
            </span>
            <ProgressBar now={100} animated striped aria-label="Creating the orders" />
          </>
        ) : progress ? (
          <div data-testid="marketing-booking-progress">
            <span className="ao-run-line">
              Booking carriers: {progress.done} / {progress.total}
              {left ? <span className="ao-run-eta"> · {left}</span> : null}
            </span>
            <ProgressBar
              now={progress.done}
              max={progress.total}
              animated
              striped
              aria-label={`Booking carriers: ${progress.done} of ${progress.total}`}
            />
          </div>
        ) : active ? (
          <>
            <span className="ao-run-line">
              {checkingCarrier
                ? 'Reading the orders again at the carrier…'
                : `Booking ${plural(inFlight.size, 'order')}…`}
            </span>
            <ProgressBar now={100} animated striped aria-label="Working on the orders" />
          </>
        ) : attention === 0 ? (
          <div className="ao-run-done is-ok">
            <CheckIcon />
            <span>
              {orders.length === 1
                ? `The order is ${external ? 'in OMS with its waybill' : 'booked'}.`
                : `All ${plural(orders.length, 'order')} are ${external ? 'in OMS with their waybills' : 'booked'}.`}{' '}
              They are on Orders as MKT orders.
            </span>
          </div>
        ) : (
          <div className="ao-run-done is-act">
            <WarningIcon />
            <span>
              {booked} of {plural(orders.length, 'order')} {external ? 'recorded' : 'booked'} ·{' '}
              <strong>{attention} need you</strong>. They are listed under Needs you below.
            </span>
          </div>
        )}
      </div>
      {!creating && (
        <>
          <div className="ao-tiles" role="group" aria-label="Show orders">
            {tiles.map((tile) => (
              <button
                key={tile.key}
                type="button"
                className={`ao-tile ${tile.tone}${filter === tile.key ? ' is-on' : ''}`}
                aria-pressed={filter === tile.key}
                onClick={() => setFilter(tile.key)}
              >
                <span className="ao-tile-value">{tile.value}</span>
                <span className="ao-tile-label">{tile.label}</span>
              </button>
            ))}
          </div>
          <div className="ao-result-bar">
            <span className="ao-result-count" data-testid="marketing-result-count">
              {countLine(orders, inFlight)}
            </span>
            {waiting > 0 && (
              <Button size="sm" variant="primary" disabled={bulkBooking} onClick={onBookAll}>
                {bulkBooking ? 'Booking…' : `Book all not attempted (${waiting})`}
              </Button>
            )}
            {checking > 0 && (
              <Button size="sm" variant="outline-secondary" disabled={checkingCarrier} onClick={onCheckAgain}>
                {checkingCarrier ? 'Checking…' : `Check carrier again (${checking})`}
              </Button>
            )}
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={active}
              onClick={() => downloadResult(orders, fileName)}
            >
              <DownloadIcon />
              Download result (.csv)
            </Button>
            <Button size="sm" variant="outline-secondary" disabled={active || bulkBooking} onClick={onImportAnother}>
              <UploadIcon />
              Import another file
            </Button>
            {waiting > 0 && !running && (
              <div className="ao-result-help text-muted small">
                Not attempted: no carrier has been asked about these yet, so they are booked together. A YunExpress
                timeout keeps its own Book again: look it up in YUN OMS first, it may already have a waybill.
              </div>
            )}
          </div>
          <div className="add-orders-scroll ao-result-scroll">
            <Table
              size="sm"
              className="add-orders-table ao-result mb-0 align-middle"
              aria-label="Marketing import result"
            >
              <colgroup>
                <col className="ao-order" />
                <col className="ao-ext" />
                <col className="ao-result-carrier" />
                <col className="ao-waybill" />
                <col className="ao-status" />
                <col />
                <col className="ao-action" />
              </colgroup>
              <thead>
                <tr>
                  <th>Order</th>
                  <th>External No.</th>
                  <th>Carrier</th>
                  <th>Waybill</th>
                  <th>Status</th>
                  <th>Reason</th>
                  <th>
                    <span className="visually-hidden">Action</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.map((item) => {
                  const booking = item.carrierBooking;
                  const status = booking ? BOOKING_STATUS[booking.result] : undefined;
                  const busyRow = inFlight.has(item.orderId);
                  return (
                    <tr key={item.orderId}>
                      {/* Linked, because a marketing order sorts below the web orders and is not on the list's first page. */}
                      <td>
                        <Link to={ROUTES.fulfillment.orderDetail(item.orderId)} className="font-monospace">
                          #{item.orderId}
                        </Link>{' '}
                        <span className="text-muted small">{item.created ? 'created' : 'existing'}</span>
                      </td>
                      <td className="font-monospace">{item.externalOrderNumber ?? EMPTY_CELL}</td>
                      <td>{booking?.carrier ?? EMPTY_CELL}</td>
                      <td className="font-monospace">{booking?.trackingNumber ?? EMPTY_CELL}</td>
                      <td className={busyRow ? 'text-muted' : status?.tone}>
                        {busyRow ? 'Booking…' : (status?.label ?? EMPTY_CELL)}
                      </td>
                      {/* While its booking is with the server, the row's old reason and its Book again say nothing true. */}
                      <td className="small">
                        {booking?.error && !busyRow ? <CarrierErrorText message={booking.error} /> : ''}
                      </td>
                      <td>
                        {booking?.result === 'FAILED' && !busyRow && (
                          <Button
                            size="sm"
                            variant="outline-primary"
                            disabled={busyRow}
                            aria-label={`Book again — order ${item.orderId}`}
                            onClick={() => onBookAgain(item.orderId)}
                          >
                            {busyRow ? 'Booking…' : 'Book again'}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {shown.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center text-muted">
                      No orders here.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </>
      )}
    </section>
  );
}

interface MarketingImportFormProps {
  readonly client: FulfillmentAdminClient;
  /** True only while a confirm is with the server: that write cannot be called back, so the modal holds. */
  readonly onConfirmingChange: (confirming: boolean) => void;
  readonly onImported: () => void;
}

function MarketingImportForm({ client, onConfirmingChange, onImported }: MarketingImportFormProps) {
  const [template, setTemplate] = useState('');
  /** What each column is for; empty from a server that sends the header alone. */
  const [columns, setColumns] = useState<MarketingImportColumn[]>([]);
  const [csv, setCsv] = useState('');
  const [preview, setPreview] = useState<MarketingImportPreview | null>(null);
  const [orders, setOrders] = useState<MarketingImportOrderResult[]>([]);
  /** Named only when the rows came from a file; pasted rows have no filename to record the batch under. */
  const [fileName, setFileName] = useState('');
  const [duplicateOf, setDuplicateOf] = useState<MarketingImportDuplicate | null>(null);
  const [batches, setBatches] = useState<MarketingImportBatch[]>([]);
  const [batchesError, setBatchesError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** The first read failing is a state the form stays in; every write reports through the toast [T03]. */
  const [loadError, setLoadError] = useState<string | null>(null);
  const { toasts, showError, showFailure, dismiss } = useFulfillmentToast();
  const { user } = useAuth();
  const [usingCustomType, setUsingCustomType] = useState(false);
  const [customOrderType, setCustomOrderType] = useState('');
  /** Null until the desk edits it, so the box follows the logged-in user once the session has loaded. */
  const [picDraft, setPicDraft] = useState<string | null>(null);
  /** Blank until the desk names the carrier of a file booked outside; nothing is assumed for it. */
  const [externalCarrier, setExternalCarrier] = useState<MarketingWaybillCarrier | ''>('');
  const [stockSource, setStockSource] = useState<MarketingStockSource>('FF2_STOCK');
  /** The Exceptions fold; it opens by itself when the brand sends the goods, so that choice is never folded out of sight. */
  const [exceptionsOpen, setExceptionsOpen] = useState(false);
  /** Set once the server refused AUTO: every later preview of this form asks with FF2 straight away. */
  const autoRefusedRef = useRef(false);
  /** The Recent imports batch whose goods the desk is receiving; null while the arrival dialog is shut. */
  const [arrivalBatch, setArrivalBatch] = useState<MarketingImportBatch | null>(null);
  /** Orders whose Book again is with the server; the screen greys their button. */
  const [rebooking, setRebooking] = useState<ReadonlySet<number>>(new Set());
  /** The same orders, read synchronously: two presses in one tick both see the state from before either. */
  const rebookingRef = useRef(new Set<number>());
  /** True while "Book all not attempted" works through its orders; one run at a time. */
  const [bulkBooking, setBulkBooking] = useState(false);
  /** True while the Check carrier orders are read again. */
  const [checkingCarrier, setCheckingCarrier] = useState(false);
  /** How far the running booking run has got; null when none runs. */
  const [progress, setProgress] = useState<BookingProgress | null>(null);
  /** What the confirm is turning into orders right now; null when no confirm is with the server. */
  const [creating, setCreating] = useState<CreatingRun | null>(null);
  /**
   * The run on screen and what it was: a new confirm or a Book missing starts a fresh panel (its filter back on All),
   * and a past batch is described by its own file, not by whatever the form holds now.
   */
  const [run, setRun] = useState<{ key: number; external: boolean; campaignSupplied: boolean; fileName: string }>({
    key: 0,
    external: false,
    campaignSupplied: false,
    fileName: '',
  });
  /** The Recent imports batch whose missing waybills are being booked, so its own button can show how far it got. */
  const [bookingBatchId, setBookingBatchId] = useState<string | null>(null);
  /** One booking run at a time, read synchronously: the confirm's own run and a press of Book all must not overlap. */
  const bulkRef = useRef(false);
  /** The preview typed or pasted rows are waiting for (PREVIEW_DELAY_MS). */
  const previewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** The carrier and stock choices as of the last press, read by a file read that finishes after the desk changed them. */
  const bookingRef = useRef<MarketingImportBookingOptions>(bookingOf(''));
  /** The choices the rows on screen were judged by, as sent; the confirm sends exactly these. */
  const previewBookingRef = useRef<MarketingImportBookingOptions>(bookingRef.current);
  /**
   * The same choices for the screen to show, with who books as the server read the file: FF2 or EXTERNAL, AUTO for a
   * file that mixes the two, and AUTO before any preview. A new file starts it over; a re-preview keeps it until it lands.
   */
  const [previewChoices, setPreviewChoices] = useState<MarketingImportBookingOptions>(bookingRef.current);
  const previewRevisionRef = useRef(0);
  // The last unreadable-file card, closed by the next pick so a corrected file does not sit under an old error.
  const readFailureToastRef = useRef<number | null>(null);
  const batchesRequestRef = useRef(0);
  const external = previewChoices.bookingMode === 'EXTERNAL';
  /** A two-letter code is not a country to most people, so the name rides with it. */
  const blockedNames = (preview?.blockedCountries ?? [])
    .map((country) => `${country.name} (${country.code})`)
    .join(', ');
  const blocked = (preview?.blockedCountries.length ?? 0) > 0;
  /** One type and one PIC cover the whole batch; every order the confirm creates is recorded under them. */
  const orderType = usingCustomType ? customOrderType.trim() : SEEDING_ORDER_TYPE;
  // An import is recorded under whoever raised it unless the desk names someone else.
  const picInput = picDraft ?? (user?.name || user?.email || '');
  const pic = picInput.trim();
  const batchComplete = orderType !== '' && pic !== '';
  /**
   * Nothing is imported while any row is refused or any destination has no carrier: the server refuses
   * the file whole, so the form has to stop before the desk spends a confirm finding that out.
   */
  const canConfirm =
    (preview?.rows.length ?? 0) > 0 && (preview?.rowErrors.length ?? 0) === 0 && !blocked && batchComplete;
  /** The orders the rows fold into: several lines for one recipient and address are one parcel. */
  const orderCount = preview?.mergeNotice?.orders ?? preview?.rows.length ?? 0;
  /** From Confirm on, the import itself is what the dialog is about; the rows it came from fold away. */
  const runActive = creating !== null || orders.length > 0;
  const sheetTitle = preview
    ? `Preview${fileName ? ` — ${fileName}` : ''}${preview.sheetType ? ` · ${SHEET_LABEL[preview.sheetType]}` : ''} · ${plural(preview.rows.length, 'row')} parsed · ${plural(preview.rowErrors.length, 'error')}`
    : '';

  /**
   * Closing the modal unmounts the form while a file read or a preview may still be on its way: moving the
   * revisions on makes those late answers stale, and a closed form must not leave the modal held.
   */
  useEffect(
    () => () => {
      if (previewTimerRef.current) {
        clearTimeout(previewTimerRef.current);
      }
      previewRevisionRef.current += 1;
      batchesRequestRef.current += 1;
      onConfirmingChange(false);
    },
    [onConfirmingChange],
  );

  useEffect(() => {
    let live = true;
    client.getMarketingImportTemplate().then(
      (response) => {
        if (live) {
          setTemplate(response.template);
          setColumns(response.columns ?? []);
          setLoadError(null);
        }
      },
      (error: unknown) => {
        if (live) {
          setLoadError(describeFailure(error, GENERIC_ERROR));
        }
      },
    );
    return () => {
      live = false;
    };
  }, [client]);

  /** Kept apart from the template read: the desk can still import while the record of past imports is down. */
  const loadBatches = useCallback(async () => {
    // A late first read must not replace the list a confirm already re-read.
    const request = ++batchesRequestRef.current;
    try {
      const response = await client.listMarketingImportBatches(RECENT_IMPORTS);
      if (request !== batchesRequestRef.current) {
        return;
      }
      setBatches(response.items);
      setBatchesError(null);
    } catch (error) {
      if (request !== batchesRequestRef.current) {
        return;
      }
      setBatchesError(describeFailure(error, 'Could not load recent imports.'));
    }
  }, [client]);

  useEffect(() => {
    void loadBatches();
  }, [loadBatches]);

  /**
   * Discards whatever the last preview said, because the rows it judged are no longer the rows on
   * screen, and returns the revision that now owns the form so a slower read can tell it was replaced.
   */
  const resetPreview = (): number => {
    setPreview(null);
    setOrders([]);
    setDuplicateOf(null);
    setPreviewChoices(bookingRef.current);
    return ++previewRevisionRef.current;
  };

  const runPreview = async (sourceCsv: string, requested = bookingRef.current) => {
    if (!sourceCsv.trim()) {
      return;
    }
    let booking = autoRefusedRef.current ? withMode(requested, 'FF2') : requested;
    const revision = ++previewRevisionRef.current;
    setBusy(true);
    setPreview(null);
    setOrders([]);
    setDuplicateOf(null);
    try {
      // Extra columns and "1,000" quantities are the sheet's formatting, not the desk's mistake. The YunExpress bulk
      // sheet has columns of its own, which the server reads by their names, so it goes as it was read (SSD-7457).
      const body = isYunExpressBulkSheet(sourceCsv) ? sourceCsv : alignToTemplate(sourceCsv, template);
      let result: MarketingImportPreview;
      try {
        result = await client.previewMarketingImport(body, booking);
      } catch (error) {
        if (!autoRefused(booking, error)) {
          throw error;
        }
        autoRefusedRef.current = true;
        booking = withMode(booking, 'FF2');
        result = await client.previewMarketingImport(body, booking);
      }
      if (revision === previewRevisionRef.current) {
        previewBookingRef.current = booking;
        setPreviewChoices({ ...booking, bookingMode: result.bookingMode ?? booking.bookingMode });
        setPreview(result);
      }
    } catch (error) {
      // No file is judged now, so the line and the carrier select stop describing the one judged before.
      if (revision === previewRevisionRef.current) {
        setPreviewChoices(requested);
      }
      showFailure(error, GENERIC_ERROR);
    } finally {
      setBusy(false);
    }
  };

  /**
   * The rows of a file booked outside were judged under the carrier of their waybills, so a new carrier judges them again
   * rather than confirming rows the screen no longer describes.
   */
  const chooseCarrier = (carrier: MarketingWaybillCarrier | '') => {
    bookingRef.current = bookingOf(carrier, stockSource);
    setExternalCarrier(carrier);
    if (preview && !previewTimerRef.current) {
      void runPreview(csv, bookingRef.current);
    }
  };

  /**
   * The stock source decides whether the preview checks FF2 stock at all (a campaign-supplied batch takes none), so a new
   * choice judges the rows again, exactly as a new carrier does.
   */
  const chooseStockSource = (source: MarketingStockSource) => {
    bookingRef.current = bookingOf(externalCarrier, source);
    setStockSource(source);
    if (source === 'CAMPAIGN_SUPPLIED') {
      setExceptionsOpen(true);
    }
    if (preview && !previewTimerRef.current) {
      void runPreview(csv, bookingRef.current);
    }
  };

  /**
   * Books one order the import could not, through Order Detail's Create Carrier Order. Only a FAILED order offers it:
   * no parcel was made there, while an UNKNOWN one may already have one at the carrier.
   */
  const bookAgain = async (orderId: number) => {
    if (rebookingRef.current.has(orderId)) {
      return;
    }
    const operator = OperatorIdentity.current() ?? OperatorIdentity.handleOf(user?.email, user?.name);
    if (!operator) {
      showError('Sign in again to book: the carrier order records who booked it.');
      return;
    }
    const settle = (change: (order: MarketingImportOrderResult) => MarketingImportOrderResult) =>
      setOrders((current) => current.map((order) => (order.orderId === orderId ? change(order) : order)));
    rebookingRef.current.add(orderId);
    setRebooking(new Set(rebookingRef.current));
    try {
      const created = await client.createCarrierOrder(orderId, { operator });
      settle((order) => ({
        ...order,
        carrierBooking: {
          orderId,
          result: 'BOOKED',
          state: 'BOOKED',
          carrier: created.carrier,
          trackingNumber: created.trackingNumber || created.carrierOrderId,
          error: null,
        },
      }));
    } catch (error) {
      const reason = describeFailure(error, 'The carrier order could not be created.');
      // A carrier has been asked now, so the order leaves "not attempted" for good; one that may hold a parcel turns to
      // Check carrier, so neither Book again nor the bulk button can book it a second time.
      const unknown = mayHaveBooked(error);
      settle((order) =>
        order.carrierBooking
          ? {
              ...order,
              carrierBooking: {
                ...order.carrierBooking,
                result: unknown ? 'UNKNOWN' : order.carrierBooking.result,
                state: order.carrierBooking.state ?? 'FAILED',
                error: unknown ? `${reason} It may have reached the carrier: press Check carrier again.` : reason,
              },
            }
          : order,
      );
    } finally {
      rebookingRef.current.delete(orderId);
      setRebooking(new Set(rebookingRef.current));
    }
  };

  /**
   * Books every order no carrier was asked about, a few at a time through the marketing booking route, writing each
   * answer into its row as it lands (SSD-7512). A YunExpress timeout or a carrier refusal is left to its own Book again.
   * A request that fails on the way may still have reached the carrier for some of its orders, so those rows turn to
   * Check carrier rather than back to Failed.
   */
  const bookOrders = async (requested: number[]) => {
    const orderIds = requested.filter((orderId) => !rebookingRef.current.has(orderId));
    if (bulkRef.current || orderIds.length === 0) {
      return;
    }
    // A new file, a new preview or a closed dialog moves the revision on: the run then stops between requests.
    const revision = previewRevisionRef.current;
    bulkRef.current = true;
    setBulkBooking(true);
    const startedAt = Date.now();
    setProgress({ done: 0, total: orderIds.length, startedAt });
    orderIds.forEach((orderId) => rebookingRef.current.add(orderId));
    setRebooking(new Set(rebookingRef.current));
    const chunks: number[][] = [];
    for (let start = 0; start < orderIds.length; start += REBOOK_CHUNK) {
      chunks.push(orderIds.slice(start, start + REBOOK_CHUNK));
    }
    let done = 0;
    // One chunk's request: its answers land in the rows; true when the carrier or the route answered rate limited.
    const bookChunk = async (chunk: number[]): Promise<boolean> => {
      try {
        const bookings = (await client.bookMarketingCarrierOrders(chunk)).orders;
        const answered = new Map(bookings.map((booking) => [booking.orderId, booking]));
        setOrders((current) =>
          current.map((order) => {
            const booking = answered.get(order.orderId);
            return booking ? { ...order, carrierBooking: booking } : order;
          }),
        );
        return bookings.some((booking) => looksRateLimited(booking.error));
      } catch (error) {
        const reason = describeFailure(error, 'The booking request did not come back.');
        // Refused at the door, the orders were never booked and wait for the next press; otherwise some may have been.
        const unknown = mayHaveBooked(error);
        setOrders((current) =>
          current.map((order) =>
            chunk.includes(order.orderId) && order.carrierBooking
              ? {
                  ...order,
                  carrierBooking: unknown
                    ? {
                        ...order.carrierBooking,
                        result: 'UNKNOWN',
                        error: `${reason} It may have reached the carrier: press Check carrier again before booking it.`,
                      }
                    : { ...order.carrierBooking, error: reason },
                }
              : order,
          ),
        );
        return looksRateLimited(reason);
      } finally {
        chunk.forEach((orderId) => rebookingRef.current.delete(orderId));
        setRebooking(new Set(rebookingRef.current));
        done += chunk.length;
        setProgress({ done: Math.min(done, orderIds.length), total: orderIds.length, startedAt });
      }
    };
    try {
      const started = await runBookingLanes(
        chunks,
        BOOKING_LANES,
        bookChunk,
        () => revision !== previewRevisionRef.current,
      );
      if (started < chunks.length) {
        chunks
          .slice(started)
          .flat()
          .forEach((orderId) => rebookingRef.current.delete(orderId));
        setRebooking(new Set(rebookingRef.current));
      }
    } finally {
      bulkRef.current = false;
      setBulkBooking(false);
      setProgress(null);
    }
  };

  /**
   * Books what a past import is still missing (SSD-7512): every order of the batch goes through the marketing booking
   * route, which answers an order that already holds a parcel as Booked and books one only when no carrier was asked
   * or a booking failed cleanly. This is how a run the dialog lost (a closed tab, a confirm whose answer never came back)
   * is finished, with the same progress bar.
   */
  const bookBatch = async (batch: MarketingImportBatch) => {
    // Not while a Book again or a carrier check is still answering into the rows this run would replace.
    if (bulkRef.current || busy || rebookingRef.current.size > 0 || checkingCarrier || batch.orderIds.length === 0) {
      return;
    }
    setOrders(
      batch.orderIds.map((orderId) => ({
        orderId,
        created: false,
        carrierBooking: {
          orderId,
          result: 'FAILED',
          state: null,
          carrier: null,
          trackingNumber: null,
          error: 'Waiting to be booked.',
        },
      })),
    );
    onConfirmingChange(true);
    // Book missing books through the marketing route, which answers a parcel booked outside as booked.
    setRun((current) => ({
      key: current.key + 1,
      external: false,
      campaignSupplied: false,
      fileName: batch.filename ?? '',
    }));
    setBookingBatchId(batch.batchId);
    try {
      await bookOrders(batch.orderIds);
    } finally {
      setBookingBatchId(null);
      onConfirmingChange(false);
      void loadBatches();
    }
  };

  /** The Book all button: every order no carrier was asked about, as the table stands now. */
  const bookAllNotAttempted = () =>
    bookOrders(orders.filter((order) => notAttempted(order.carrierBooking)).map((order) => order.orderId));

  /**
   * Reads each Check carrier order again (SSD-7512). The confirm stops waiting when its time budget runs out, but a
   * carrier call already under way goes on and records its own waybill, so the order itself is the answer: a carrier
   * order on its outbound tracking turns the row to Booked, and none leaves it to check, with the time it was read.
   * [SSD-7905] A Deleonex booking with no courier waybill yet is a pending parcel, not a tracking row: the parcel is
   * resolved the way Order Detail resolves it, so a pending parcel with a carrier order counts as Booked too, unless
   * Deleo flags it as needing attention (held or cancelled there), which stays to check.
   */
  const checkCarrierAgain = async () => {
    const orderIds = orders.filter((order) => order.carrierBooking?.result === 'UNKNOWN').map((order) => order.orderId);
    if (checkingCarrier || orderIds.length === 0) {
      return;
    }
    const revision = previewRevisionRef.current;
    setCheckingCarrier(true);
    try {
      for (const orderId of orderIds) {
        if (revision !== previewRevisionRef.current) {
          break;
        }
        let change: (booking: MarketingCarrierBooking) => MarketingCarrierBooking;
        try {
          const detail = await client.getOrderDetail(orderId);
          // The order's current outbound shipment, as Order Detail reads it: its carrier order is written once the carrier
          // answered. [SSD-7905] A pending Deleonex parcel comes first and has no courier waybill, so its number is the
          // carrier order; one Deleo flags as needing attention is not counted as booked.
          const parcel = currentOutboundShipmentOf(detail.tracking ?? [], orderId, detail.pendingParcels ?? []);
          const carrierOrderId = parcel?.carrierOrderId?.trim() ?? '';
          const readAt = kstHourMinute(new Date().toISOString());
          if (parcel && isPendingParcel(parcel) && parcel.needsAttention !== false) {
            change = (booking) => ({
              ...booking,
              error: `${DELEONEX_NEEDS_ATTENTION} (read ${readAt}). Check it at Deleo before booking it again.`,
            });
          } else if (parcel && carrierOrderId) {
            change = (booking) => ({
              ...booking,
              result: 'BOOKED',
              carrier: parcel.carrier,
              trackingNumber: isPendingParcel(parcel) ? carrierOrderId : parcel.number || carrierOrderId,
              error: null,
            });
          } else {
            change = (booking) => ({
              ...booking,
              error: `No carrier order on the order yet (read ${readAt}). Look it up at the carrier before booking it again.`,
            });
          }
        } catch (error) {
          const reason = describeFailure(error, 'The order could not be read.');
          change = (booking) => ({ ...booking, error: reason });
        }
        setOrders((current) =>
          current.map((order) =>
            order.orderId === orderId && order.carrierBooking?.result === 'UNKNOWN'
              ? { ...order, carrierBooking: change(order.carrierBooking) }
              : order,
          ),
        );
      }
    } finally {
      setCheckingCarrier(false);
    }
  };

  /** A preview the box scheduled for typed rows; a file or a new booking choice takes over from it. */
  const cancelScheduledPreview = () => {
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
      previewTimerRef.current = null;
    }
  };

  /** The next file starts from an empty box; the batch type, PIC, carrier and stock source stay as the desk set them. */
  const importAnother = () => {
    cancelScheduledPreview();
    resetPreview();
    setCsv('');
    setFileName('');
  };

  /** A file and a paste meet here: the box shows what was read, so the rest of the form sees one input. */
  const onFile = async (file: File | undefined) => {
    if (!file) {
      return;
    }
    cancelScheduledPreview();
    if (readFailureToastRef.current !== null) {
      dismiss(readFailureToastRef.current);
      readFailureToastRef.current = null;
    }
    // Re-picking after a mis-click is normal, and a workbook can still be reading when the second
    // file arrives — whichever read finishes last, the desk's latest choice is the one that stands.
    const revision = resetPreview();
    let text: string;
    try {
      text = await readAsCsv(file);
    } catch {
      if (revision === previewRevisionRef.current) {
        // A failed read must not leave the box showing the file picked before as if it were this one (SSD-7778).
        if (fileName !== '') {
          setCsv('');
          setFileName('');
        }
        readFailureToastRef.current = showError(
          'Could not read that file. Save it as .csv, .xlsx, or .xls and try again.',
        );
      }
      return;
    }
    if (revision !== previewRevisionRef.current) {
      return;
    }
    setCsv(text);
    setFileName(file.name);
    await runPreview(text);
  };

  const confirm = async (
    overrideDuplicate = false,
    mayDefer = true,
    booking = previewBookingRef.current,
  ): Promise<void> => {
    if (!preview || !canConfirm) {
      return;
    }
    const revision = previewRevisionRef.current;
    const rows = preview.rows;
    // Who books is what the server read the file as; under AUTO the confirm reads it again the same way.
    const resolved = previewChoices.bookingMode;
    // When OMS books, the server is asked to leave the bookings to this screen, which runs them with a progress bar.
    const deferBooking = mayDefer && resolved !== 'EXTERNAL';
    setBusy(true);
    onConfirmingChange(true);
    setDuplicateOf(null);
    setCreating({ rows: rows.length, orders: preview.mergeNotice?.orders ?? rows.length, again: overrideDuplicate });
    setRun((current) => ({
      key: current.key + 1,
      external: resolved === 'EXTERNAL',
      campaignSupplied: booking.stockSource === 'CAMPAIGN_SUPPLIED',
      fileName,
    }));
    try {
      const result = await client.confirmMarketingImport(rows, {
        orderType,
        picUser: pic,
        // The hash the preview took is the only thing that lets the server recognise a re-upload.
        fileHash: preview.fileHash,
        ...(fileName ? { filename: fileName } : {}),
        ...(overrideDuplicate ? { overrideDuplicate: true } : {}),
        // The confirm judges the rows by the same booking choice the preview judged them by.
        ...booking,
        ...(deferBooking ? { deferBooking: true } : {}),
      });
      setCreating(null);
      // Who booked them as the confirm itself resolved it, where the server says; the preview's reading otherwise.
      if (result.bookingMode) {
        const external = result.bookingMode === 'EXTERNAL';
        setRun((current) => ({ ...current, external }));
      }
      // A server that predates the deferral books them itself and answers each booking, so nothing is left to run.
      const deferred = result.orders
        .filter((order) => deferredByServer(order.carrierBooking))
        .map((order) => order.orderId);
      if (revision === previewRevisionRef.current) {
        setOrders(
          result.orders.map((order) =>
            order.carrierBooking && deferredByServer(order.carrierBooking)
              ? {
                  ...order,
                  carrierBooking: { ...order.carrierBooking, result: 'FAILED', error: 'Waiting to be booked.' },
                }
              : order,
          ),
        );
      }
      onImported();
      void loadBatches();
      if (deferred.length > 0 && revision === previewRevisionRef.current) {
        await bookOrders(deferred);
        // Recent imports counts each batch's orders still without a waybill, which the run just changed.
        void loadBatches();
      }
    } catch (error) {
      // A server that answered the preview under AUTO and then refuses it on the confirm is told what it read the file as.
      if (autoRefused(booking, error)) {
        autoRefusedRef.current = true;
        return await confirm(
          overrideDuplicate,
          mayDefer,
          withMode(booking, resolved === 'EXTERNAL' ? 'EXTERNAL' : 'FF2'),
        );
      }
      if (deferBooking && deferralRefused(error)) {
        return await confirm(overrideDuplicate, false, booking);
      }
      // A file already imported is a warning, not a refusal: nothing was created and the desk decides.
      const alreadyImported = duplicateFrom(error);
      if (alreadyImported) {
        setDuplicateOf(alreadyImported);
        return;
      }
      showFailure(error, GENERIC_ERROR);
    } finally {
      setCreating(null);
      setBusy(false);
      onConfirmingChange(false);
    }
  };

  /** The previewed rows and what was found in them; the same block before Confirm and folded after it. */
  const previewBody = preview ? (
    <>
      <div className="add-orders-scroll mt-2">
        <Table
          size="sm"
          hover
          className="add-orders-table ao-preview mb-0 align-middle"
          aria-label="Marketing import preview"
        >
          <colgroup>
            <col className="ao-recipient" />
            <col className="ao-country" />
            <col className="ao-sku" />
            <col />
            <col className="ao-qty" />
            <col className="ao-campaign" />
            <col className="ao-carrier" />
            <col className="ao-waybill" />
          </colgroup>
          <thead>
            <tr>
              <th>Recipient</th>
              <th>Country · State</th>
              <th>SKU</th>
              <th>Product Name</th>
              <th>Qty</th>
              <th>Campaign / Ext. No.</th>
              <th>{external ? 'Carrier' : 'Carrier (auto)'}</th>
              <th>Waybill</th>
            </tr>
          </thead>
          <tbody>
            {preview.rows.slice(0, PREVIEW_ROWS).map((row, index) => (
              <tr key={`${row.campaignId ?? row.externalOrderNumber}-${row.influencerId}-${row.sku}-${index}`}>
                <td>{row.recipientName}</td>
                {/* The state as the server will print it: a US or CA name comes back as its two-letter code. */}
                <td>
                  {row.countryCode}
                  {row.state ? ` · ${row.state}` : ''}
                </td>
                <td className="font-monospace">{row.sku}</td>
                {/* No name means the catalog does not know this sku — a typo, caught before confirming. */}
                <td className={row.productName ? undefined : 'text-danger fw-semibold'}>
                  {row.productName ?? 'Unknown SKU'}
                </td>
                <td className="font-monospace">{row.qty}</td>
                {/* A row with no campaign is named by the order team's own number (SSD-7457). */}
                <td className={row.campaignId ? undefined : 'font-monospace'}>
                  {row.campaignId ?? row.externalOrderNumber ?? EMPTY_CELL}
                </td>
                {/* A blank carrier is the reason the confirm below is refused, so it is never left implicit. */}
                <td className={row.carrier ? 'text-success fw-semibold' : 'text-danger fw-semibold'}>
                  {row.carrier ?? (external ? 'Not named' : 'Not connected')}
                </td>
                <td className="font-monospace">{row.waybill ?? EMPTY_CELL}</td>
              </tr>
            ))}
            {preview.rows.length > PREVIEW_ROWS && (
              <tr>
                <td colSpan={8} className="text-center text-muted">
                  ⋯ +{preview.rows.length - PREVIEW_ROWS} more rows
                </td>
              </tr>
            )}
            {preview.rows.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center text-muted">
                  {EMPTY_CELL}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      {/* The router also weighs declared value and product categories; a file carries no prices. */}
      <div className="text-muted small mb-2">
        {external
          ? 'Each waybill is recorded under the carrier shown. OMS books nothing for these orders.'
          : 'Carrier is decided by destination. The shipment itself may still fall to another carrier once its declared value is known.'}
      </div>
      {preview.mergeNotice && (
        <Alert variant="warning" className="py-2 mb-2">
          {mergeNoticeText(preview.mergeNotice)}
        </Alert>
      )}
      {/* Advice only: Create orders stays open, and a line stock cannot cover in full is bought instead. */}
      {(preview.stockWarnings ?? []).map((warning) => (
        <Alert variant="warning" className="py-2 mb-2" key={`stock-${warning.sku}`}>
          {stockWarningText(warning)}
        </Alert>
      ))}
      {preview.stockCheck === 'UNAVAILABLE' && (
        <Alert variant="warning" className="py-2 mb-2">
          {STOCK_CHECK_UNAVAILABLE_TEXT}
        </Alert>
      )}
      {/*
        A phone or postcode the sheet may have mangled, on parcels booked outside OMS: the waybill already exists, so it
        is advice the desk weighs, never a refusal (SSD-7512 follow-up). A server that predates it sends no field.
      */}
      {(preview.addressWarnings ?? []).length > 0 && (
        <Alert variant="warning" className="py-2 mb-2 ao-address-warnings" data-testid="marketing-address-warnings">
          <div className="fw-semibold">
            {plural(preview.addressWarnings?.length ?? 0, 'address warning')} — these do not block Confirm.
          </div>
          <ul className="mb-0">
            {(preview.addressWarnings ?? []).map((warning) => (
              <li key={`${warning.orderKey}-${warning.field}-${warning.rowNumbers.join('.')}`}>
                {addressWarningText(warning)}
              </li>
            ))}
          </ul>
        </Alert>
      )}
      {/*
    The server numbers a reason by the file's own row, header included, as the sheet shows it, and
    leaves a refused row out of the table above — so the reason cannot sit beside a table row.
  */}
      {preview.rowErrors.map((item) => (
        <Alert variant="danger" className="py-2 mb-2" key={`${item.row}-${item.message}`}>
          Row {item.row}: {item.message}
        </Alert>
      ))}
      {blocked && (
        <Alert variant="danger" className="mb-0">
          Cannot import — these countries have no connected carrier: {blockedNames}. Ask the fulfillment team to connect
          them, or remove those rows and preview again.
        </Alert>
      )}
    </>
  ) : null;

  return (
    <div className="add-orders">
      <FulfillmentToastHost toasts={toasts} onDismiss={dismiss} />
      <div className="add-orders-head">
        <p className="text-muted mb-0">
          Import manual gifting and marketing orders from a file. They join this list as marketing orders.
        </p>
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={!template}
          onClick={() => downloadMarketingImportTemplate(template)}
        >
          <DownloadIcon />
          Download template (.xlsx)
        </Button>
      </div>
      {/* The file is the only form here, so what a carrier label needs is said before a preview refuses a row (SSD-7448). */}
      {columns.length > 0 && (
        <details className="add-orders-columns">
          <summary>
            Columns in the file · {columns.filter((column) => column.need === 'required').length} required,{' '}
            {columns.filter((column) => column.need === 'conditional').length} in some cases,{' '}
            {columns.filter((column) => column.need === 'optional').length} optional
          </summary>
          <Table size="sm" className="add-orders-table mb-0" aria-label="Marketing import columns">
            <colgroup>
              <col className="ao-col-name" />
              <col className="ao-col-need" />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th>Column</th>
                <th>Needed</th>
                <th>What it is</th>
              </tr>
            </thead>
            <tbody>
              {columns.map((column) => (
                <tr key={column.name}>
                  <td className="font-monospace">{column.name}</td>
                  <td className={column.need === 'optional' ? 'text-muted' : 'fw-semibold'}>
                    {NEED_LABEL[column.need]}
                  </td>
                  <td>{column.note}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </details>
      )}
      {loadError && (
        <Alert variant="danger" role="alert">
          {loadError}
        </Alert>
      )}
      <Row className="g-3 mb-3">
        <Col md="auto">
          <div className="form-label" id="marketing-order-type-label">
            Order type
          </div>
          <div className="d-flex flex-wrap gap-2 align-items-start">
            <ButtonGroup size="sm" aria-labelledby="marketing-order-type-label">
              <Button
                variant="outline-secondary"
                active={!usingCustomType}
                aria-pressed={!usingCustomType}
                disabled={busy}
                onClick={() => setUsingCustomType(false)}
              >
                {SEEDING_ORDER_TYPE}
              </Button>
              <Button
                variant="outline-secondary"
                active={usingCustomType}
                aria-pressed={usingCustomType}
                aria-label="Custom order type"
                disabled={busy}
                onClick={() => setUsingCustomType(true)}
              >
                <PencilIcon />
                Custom
              </Button>
            </ButtonGroup>
            {usingCustomType && (
              <div className="add-orders-custom-type">
                <Form.Control
                  size="sm"
                  aria-label="Order type name"
                  placeholder="e.g. Pop-up event giveaway"
                  maxLength={BATCH_FIELD_MAX_LENGTH}
                  value={customOrderType}
                  disabled={busy}
                  isInvalid={orderType === ''}
                  onChange={(event) => setCustomOrderType(event.target.value)}
                />
                {orderType === '' && <Form.Control.Feedback type="invalid">Enter an order type.</Form.Control.Feedback>}
              </div>
            )}
          </div>
        </Col>
        <Col md={4}>
          <Form.Group controlId="marketing-pic">
            <Form.Label>PIC</Form.Label>
            <Form.Control
              size="sm"
              maxLength={BATCH_FIELD_MAX_LENGTH}
              value={picInput}
              disabled={busy}
              isInvalid={pic === ''}
              onChange={(event) => setPicDraft(event.target.value)}
            />
            {pic === '' && <Form.Control.Feedback type="invalid">Enter a PIC name.</Form.Control.Feedback>}
            <Form.Text className="text-muted">
              Defaults to you. Recorded as the PIC of every order in this import.
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>
      {/*
        Who books is read off the file, not chosen (SSD-7596): the line says what the preview found, and the carrier of the
        waybills is asked only once a file turns out to carry them.
      */}
      <Row className="g-3 mb-3">
        <Col md="auto">
          <Form.Text
            as="div"
            className={`add-orders-booking-help${previewChoices.bookingMode === 'AUTO' ? ' text-muted' : ''}`}
            data-testid="marketing-booking-line"
          >
            {BOOKING_LINE[previewChoices.bookingMode]}
          </Form.Text>
        </Col>
        {external && (
          <Col md={3}>
            <Form.Group controlId="marketing-external-carrier">
              <Form.Label>Carrier of the waybills</Form.Label>
              <Form.Select
                size="sm"
                value={externalCarrier}
                disabled={busy}
                onChange={(event) => chooseCarrier(event.target.value as MarketingWaybillCarrier | '')}
              >
                <option value="">Choose the carrier…</option>
                {WAYBILL_CARRIERS.map((carrier) => (
                  <option key={carrier.value} value={carrier.value}>
                    {carrier.label}
                  </option>
                ))}
              </Form.Select>
              {/* The server's refusal reads "missing carrier — … or choose the carrier for the whole batch". */}
              <Form.Text className="text-muted">
                For the whole batch. A row that names its own carrier keeps it.
              </Form.Text>
            </Form.Group>
          </Col>
        )}
      </Row>
      {/*
        Where the goods come from is the batch's too, and the preview checks FF2 stock only for FF2 stock (SSD-7458). The
        brand sending them is the exception, so the choice folds away under FF2 stock, the default (SSD-7596).
      */}
      <details
        className="ao-sheet-fold mb-3"
        open={exceptionsOpen}
        onToggle={(event) => setExceptionsOpen(event.currentTarget.open)}
      >
        <summary>
          Exceptions — the brand sends the goods
          {stockSource === 'CAMPAIGN_SUPPLIED' ? ` · ${STOCK_SOURCE_LABEL.CAMPAIGN_SUPPLIED}` : ''}
        </summary>
        <div className="ao-sheet-fold-body">
          <div className="form-label" id="marketing-stock-source-label">
            Stock source
          </div>
          <ButtonGroup size="sm" aria-labelledby="marketing-stock-source-label">
            {STOCK_SOURCE_CHOICES.map((choice) => (
              <Button
                key={choice.source}
                variant="outline-secondary"
                active={stockSource === choice.source}
                aria-pressed={stockSource === choice.source}
                disabled={busy}
                onClick={() => chooseStockSource(choice.source)}
              >
                {choice.label}
              </Button>
            ))}
          </ButtonGroup>
          <Form.Text as="div" className="text-muted add-orders-booking-help">
            {STOCK_SOURCE_CHOICES.find((choice) => choice.source === stockSource)?.help}
          </Form.Text>
        </div>
      </details>
      <Form.Group controlId="marketing-file">
        <Form.Label>Upload marketing file</Form.Label>
        <Form.Control
          type="file"
          accept={MARKETING_IMPORT_ACCEPT}
          disabled={busy}
          onChange={(event) => {
            const input = event.target as HTMLInputElement;
            void onFile(input.files?.[0]);
            // Re-uploading the corrected file under the same name has to fire change again.
            input.value = '';
          }}
        />
        <Form.Text className="text-muted">
          A .csv, .xlsx or .xls is read into the box below and previewed at once: the FF2 template or the YunExpress
          bulk sheet. Paste the rows in directly when there is no file.
        </Form.Text>
      </Form.Group>
      <Form.Group controlId="marketing-csv" className="mt-3">
        <Form.Label>Marketing CSV</Form.Label>
        <Form.Control
          as="textarea"
          rows={5}
          className="font-monospace"
          value={csv}
          disabled={busy}
          onChange={(event) => {
            const typed = event.target.value;
            resetPreview();
            setCsv(typed);
            // Edited by hand, the rows are no longer the file that was uploaded under that name.
            setFileName('');
            // Previewed by itself once the desk stops typing, so Confirm is the only button to press (SSD-7512).
            cancelScheduledPreview();
            previewTimerRef.current = setTimeout(() => {
              previewTimerRef.current = null;
              void runPreview(typed);
            }, PREVIEW_DELAY_MS);
          }}
        />
      </Form.Group>
      <div className="ao-confirm-row">
        {preview && (
          <Button variant="success" disabled={busy || !canConfirm} onClick={() => void confirm()}>
            {/* 450 rows of the YunExpress sheet are 150 orders: the count the desk checks the result against (10/20). */}
            Confirm import ({plural(preview.rows.length, 'row')}
            {orderCount !== preview.rows.length ? ` → ${plural(orderCount, 'order')}` : ''})
          </Button>
        )}
        {/* What the confirm will create the orders as, read off the choices the rows on screen were judged by. */}
        {preview && (
          <span className="ao-confirm-summary" data-testid="marketing-confirm-summary">
            {BOOKING_SUMMARY[previewChoices.bookingMode]}
            {' · '}
            <span className={previewChoices.stockSource === 'CAMPAIGN_SUPPLIED' ? 'ao-campaign-supplied' : undefined}>
              {STOCK_SOURCE_LABEL[previewChoices.stockSource ?? 'FF2_STOCK']}
            </span>
          </span>
        )}
        {!preview && busy && csv.trim() && creating === null && (
          <span className="text-muted small" role="status">
            Reading the rows…
          </span>
        )}
      </div>
      {duplicateOf && (
        <Alert variant="warning" className="mt-3">
          <Alert.Heading as="h6">This file was already imported</Alert.Heading>
          <p className="mb-2">
            Imported {kstTimestamp(duplicateOf.confirmedAt)} as batch {duplicateOf.batchId} (
            {plural(duplicateOf.orderCount, 'order')}). Nothing was created just now.
          </p>
          <div className="d-flex gap-2">
            <Button size="sm" variant="outline-secondary" onClick={() => setDuplicateOf(null)}>
              Cancel
            </Button>
            <Button size="sm" variant="warning" disabled={busy} onClick={() => void confirm(true)}>
              Import anyway
            </Button>
          </div>
        </Alert>
      )}
      {/*
        The import runs and ends right under Confirm (10/20 dev test: the result used to land below the 450-row preview,
        so the desk read a finished run as stuck). The rows it came from fold away once it has started.
      */}
      {runActive && (
        <ImportRun
          key={run.key}
          orders={orders}
          creating={creating}
          progress={progress}
          inFlight={rebooking}
          bulkBooking={bulkBooking}
          checkingCarrier={checkingCarrier}
          external={run.external}
          campaignSupplied={run.campaignSupplied}
          fileName={run.fileName}
          onBookAll={() => void bookAllNotAttempted()}
          onCheckAgain={() => void checkCarrierAgain()}
          onBookAgain={(orderId) => void bookAgain(orderId)}
          onImportAnother={importAnother}
        />
      )}
      {preview &&
        (runActive ? (
          <details className="ao-sheet-fold mt-3">
            <summary>{sheetTitle}</summary>
            <div className="ao-sheet-fold-body">{previewBody}</div>
          </details>
        ) : (
          <div className="mt-3">
            <div className="fw-semibold">{sheetTitle}</div>
            {previewBody}
          </div>
        ))}
      {/* Who imported what and when outlives the toast: the server records every confirm as a batch. */}
      <h3 className="add-orders-sub">Recent imports</h3>
      {batchesError ? (
        <div className="text-danger small">{batchesError}</div>
      ) : (
        <div className="add-orders-scroll">
          <Table
            size="sm"
            hover
            className="add-orders-table ao-recent mb-0 align-middle"
            aria-label="Recent marketing imports"
          >
            <colgroup>
              <col className="ao-at" />
              <col className="ao-by" />
              <col />
              <col className="ao-type" />
              <col className="ao-pic" />
              <col className="ao-orders" />
              <col className="ao-recent-action" />
            </colgroup>
            <thead>
              <tr>
                <th>Imported at</th>
                <th>Imported by</th>
                <th>File</th>
                <th>Order type</th>
                <th>PIC</th>
                <th>Orders</th>
                <th>
                  <span className="visually-hidden">Action</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {batches.map((batch) => (
                <tr key={batch.batchId}>
                  <td className="font-monospace small">{kstTimestamp(batch.confirmedAt)}</td>
                  <td>{batch.operator ?? EMPTY_CELL}</td>
                  <td>{batch.filename ?? <span className="text-muted">Pasted rows</span>}</td>
                  <td>{batch.orderType ?? EMPTY_CELL}</td>
                  <td>{batch.picUser ?? EMPTY_CELL}</td>
                  <td className="font-monospace">
                    {batch.createdCount === batch.orderCount
                      ? batch.orderCount
                      : `${batch.createdCount} created / ${batch.orderCount}`}
                    {/* Live orders still without a waybill, which Book missing books (SSD-7525). */}
                    {(batch.waitingCount ?? 0) > 0 && (
                      <div className="ao-waiting" title="Orders of this import still without a waybill">
                        {batch.waitingCount} waiting
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="ao-recent-actions">
                      {/* A batch booked outside OMS has its waybills from the file; OMS books none of it (SSD-7596). */}
                      {batch.bookingMode !== 'EXTERNAL' && (
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          disabled={busy || bulkBooking || rebooking.size > 0 || checkingCarrier}
                          aria-label={`Book missing waybills — batch ${batch.batchId}`}
                          onClick={() => void bookBatch(batch)}
                        >
                          {bookingBatchId === batch.batchId && progress
                            ? `Booking ${progress.done}/${progress.total}`
                            : 'Book missing'}
                        </Button>
                      )}
                      {/*
                        Only where the brand sends the goods (SSD-7596). A batch recorded before the server kept its
                        stock source keeps the button: the server answers an FF2-stock batch with no orders received.
                      */}
                      {(batch.stockSource === 'CAMPAIGN_SUPPLIED' || batch.stockSource == null) && (
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          disabled={busy}
                          aria-label={`Campaign goods arrived — batch ${batch.batchId}`}
                          onClick={() => setArrivalBatch(batch)}
                        >
                          Goods arrived
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {batches.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-muted">
                    No imports yet.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      )}
      <CampaignGoodsArrivalDialog
        show={arrivalBatch !== null}
        stacked
        client={client}
        scope={arrivalBatch ? { batchId: arrivalBatch.batchId } : null}
        scopeLabel={
          arrivalBatch
            ? `import ${arrivalBatch.filename ?? 'of pasted rows'} · ${kstTimestamp(arrivalBatch.confirmedAt)}`
            : ''
        }
        onHide={() => setArrivalBatch(null)}
        onReceived={(result) => {
          // Received lines move the orders on, so the list behind the modal reads them again.
          if (result.inbounded > 0) {
            onImported();
          }
        }}
      />
    </div>
  );
}

interface AddOrdersModalProps {
  readonly show: boolean;
  readonly client: FulfillmentAdminClient;
  readonly onHide: () => void;
  /** Called once a confirm created or matched orders, so the list behind the modal can show them. */
  readonly onImported: () => void;
}

/**
 * Marketing and gifting orders are added from the Orders list they land on (SSD-7448): this used to be its
 * own Marketing & Samples screen, which the desk left the list for and came back from to find the orders.
 * The modal only mounts its form while open, so every opening starts from a clean file and a fresh read.
 */
export function AddOrdersModal({ show, client, onHide, onImported }: AddOrdersModalProps) {
  const [confirming, setConfirming] = useState(false);

  // An import in flight cannot be called back, so the modal stays until the server has answered. A preview
  // or a file read can be left: the form drops their answers once it is closed.
  const hide = () => {
    if (!confirming) {
      onHide();
    }
  };

  return (
    <Modal
      show={show}
      onHide={hide}
      size="xl"
      dialogClassName="add-orders-dialog"
      backdrop="static"
      keyboard={!confirming}
      aria-labelledby="add-orders-title"
      {...ULTIMATE_MODAL}
    >
      <Modal.Header closeButton={!confirming}>
        <Modal.Title id="add-orders-title">Add marketing orders</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <MarketingImportForm client={client} onConfirmingChange={setConfirming} onImported={onImported} />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" disabled={confirming} onClick={hide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
